// Reason: The internals of the event emitter needs to work with any boxed value.
/* eslint-disable @typescript-eslint/no-explicit-any */

export type EventHandler<T> = (event: T, id?: string) => void;

export type Disposer = () => void;

type ErrorHandler = (error: unknown) => void;

// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
export class EventEmitter<T extends Record<string, any>> {
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	private readonly errorHandlers = new WeakMap<EventHandler<any>, ErrorHandler>();
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	private events: { [key in keyof T]: EventHandler<any>[] } = Object.create(null);

	constructor() {
		// Empty
	}

	/**
	 * Register a handler for events emitted by this instance.
	 */
	public on<K extends keyof T>(event: K, handler: EventHandler<T[K]>): Disposer;
	public on<K extends keyof T>(event: K, handler: EventHandler<T[K]>, errorHandler: ErrorHandler): Disposer;
	public on<K extends keyof T>(event: K, handler: EventHandler<T[K]>, errorHandler?: ErrorHandler): Disposer {
		(this.events[event] || (this.events[event] = [])).push(handler);

		if (errorHandler) {
			this.errorHandlers.set(handler, errorHandler);
		}

		return () => {
			this.off(event, handler);
		};
	}

	/**
	 * Un-registers a handler for events emitted by this instance.
	 */
	public off<K extends keyof T>(event: K, handler: EventHandler<T[K]>): void {
		if (!this.events[event]) return;

		this.events[event].splice(this.events[event].indexOf(handler) >>> 0, 1);
	}

	public offAll(): void {
		this.events = Object.create(null);
	}

	/**
	 * Emits an event containing a given value.
	 */
	public emit<K extends keyof T>(event: K, data: T[K], id: string | undefined = undefined): void {
		(this.events[event] || []).forEach((handler) => {
			try {
				handler(data, id);
			} catch (error: unknown) {
				const errorHandler = this.errorHandlers.get(handler);
				if (errorHandler) errorHandler(error);
			}
		});
	}
}
